/**
 * Hub de Redes Sociales & Publicación — Neuralia369
 * Resumen · Redes · WhatsApp · Publicador · Studio (Crea & Publica) · Clientes
 */
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Wifi, WifiOff, Settings, RefreshCw, ExternalLink, Copy, CheckCircle,
  AlertTriangle, Plus, Send, Calendar, Clock, Loader2, Play, Pause,
  Mic, Video, Sparkles, Globe, ChevronRight, ChevronDown, X,
  Instagram, Youtube, MessageCircle, Music2, Linkedin, Twitter,
  TrendingUp, Users, BarChart3, Zap, ArrowRight, Eye, Info
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Platform metadata ────────────────────────────────────────────────────────
const PLATFORMS = {
  tiktok: {
    name: 'TikTok', color: '#010101', accent: '#fe2c55',
    gradient: 'from-[#010101] to-[#2d2d2d]',
    icon: () => <Music2 className="w-5 h-5" />,
    docsUrl: 'https://developers.tiktok.com',
    statusKey: 'tiktok',
  },
  instagram: {
    name: 'Instagram', color: '#e1306c', accent: '#833ab4',
    gradient: 'from-[#833ab4] to-[#e1306c]',
    icon: () => <Instagram className="w-5 h-5" />,
    docsUrl: 'https://developers.facebook.com/docs/instagram',
    statusKey: 'meta',
  },
  facebook: {
    name: 'Facebook', color: '#1877F2', accent: '#1877F2',
    gradient: 'from-[#1877F2] to-[#0e5cc5]',
    icon: () => <Globe className="w-5 h-5" />,
    docsUrl: 'https://developers.facebook.com',
    statusKey: 'meta',
  },
  linkedin: {
    name: 'LinkedIn', color: '#0A66C2', accent: '#0A66C2',
    gradient: 'from-[#0A66C2] to-[#085199]',
    icon: () => <Linkedin className="w-5 h-5" />,
    docsUrl: 'https://www.linkedin.com/developers',
    statusKey: 'linkedin',
    comingSoon: true,
  },
  twitter: {
    name: 'Twitter / X', color: '#000000', accent: '#1DA1F2',
    gradient: 'from-[#000000] to-[#222]',
    icon: () => <Twitter className="w-5 h-5" />,
    docsUrl: 'https://developer.twitter.com',
    statusKey: 'twitter',
    comingSoon: true,
  },
  youtube: {
    name: 'YouTube', color: '#FF0000', accent: '#FF0000',
    gradient: 'from-[#FF0000] to-[#cc0000]',
    icon: () => <Youtube className="w-5 h-5" />,
    docsUrl: 'https://developers.google.com/youtube',
    statusKey: 'youtube',
    comingSoon: true,
  },
  whatsapp: {
    name: 'WhatsApp Business', color: '#128C7E', accent: '#25D366',
    gradient: 'from-[#075e54] to-[#128C7E]',
    icon: () => <MessageCircle className="w-5 h-5" />,
    docsUrl: 'https://developers.facebook.com/docs/whatsapp',
    statusKey: 'whatsapp',
  },
};

// ─── Shared UI helpers ────────────────────────────────────────────────────────
const StatusDot = ({ connected, loading }) => {
  if (loading) return <div className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />;
  return <div className={`w-2.5 h-2.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-red-400'}`} />;
};

const Badge = ({ connected, comingSoon }) => {
  if (comingSoon) return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/20 font-bold">Próximamente</span>;
  if (connected) return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 font-bold">Conectado</span>;
  return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/20 font-bold">Desconectado</span>;
};

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="text-[#94A3B8] hover:text-white transition p-0.5">
      {copied ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
    </button>
  );
};

const SetupStep = ({ n, title, children }) => (
  <div className="flex gap-3">
    <div className="w-6 h-6 rounded-full bg-indigo-600/30 border border-indigo-500/40 flex items-center justify-center flex-shrink-0 text-indigo-400 text-xs font-black">{n}</div>
    <div className="flex-1 min-w-0">
      <p className="text-white text-xs font-semibold mb-1">{title}</p>
      <div className="text-[#94A3B8] text-xs leading-relaxed space-y-1">{children}</div>
    </div>
  </div>
);

// ─── Tab: Resumen (Overview) ──────────────────────────────────────────────────
function ResumenTab({ authFetch, statuses, statsLoading }) {
  const allPlatforms = Object.entries(PLATFORMS);
  const connected = allPlatforms.filter(([k, p]) => !p.comingSoon && statuses[p.statusKey]?.connected).length;
  const total = allPlatforms.filter(([, p]) => !p.comingSoon).length;

  return (
    <div className="space-y-6" data-testid="redes-resumen-tab">
      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Redes conectadas', value: `${connected}/${total}`, icon: Wifi, color: connected === total ? 'text-emerald-400' : 'text-amber-400' },
          { label: 'Posts programados', value: statuses.publer?.scheduled_count ?? '—', icon: Calendar, color: 'text-indigo-400' },
          { label: 'Seguidores totales', value: statuses.tiktok?.follower_count ? (statuses.tiktok.follower_count / 1000).toFixed(1) + 'K' : '—', icon: Users, color: 'text-blue-400' },
          { label: 'Estado global', value: connected > 0 ? 'Operativo' : 'Configurar', icon: TrendingUp, color: connected > 0 ? 'text-emerald-400' : 'text-red-400' },
        ].map(c => (
          <div key={c.label} className="bg-[rgba(8,15,32,0.7)] border border-[rgba(148,163,184,0.10)] rounded-2xl p-4 text-center">
            <c.icon className={`w-5 h-5 ${c.color} mx-auto mb-2`} />
            <p className={`text-2xl font-black ${c.color}`}>{statsLoading ? '…' : c.value}</p>
            <p className="text-[#94A3B8] text-[10px] mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Platform cards grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {allPlatforms.map(([key, p]) => {
          const st = statuses[p.statusKey] || {};
          const isConnected = st.connected && !p.comingSoon;
          return (
            <div key={key} className={`relative overflow-hidden rounded-2xl border p-4 space-y-3 transition ${isConnected ? 'border-emerald-500/20 bg-emerald-900/10' : p.comingSoon ? 'border-violet-500/10 bg-violet-900/5 opacity-70' : 'border-[rgba(148,163,184,0.10)] bg-[rgba(8,15,32,0.5)]'}`}
              data-testid={`platform-card-${key}`}>
              <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${p.gradient}`} />
              <div className="flex items-center justify-between">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br ${p.gradient} text-white`}>
                  <p.icon />
                </div>
                <Badge connected={isConnected} comingSoon={p.comingSoon} />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">{p.name}</p>
                {isConnected ? (
                  <p className="text-emerald-400/70 text-[10px]">{st.username ? `@${st.username}` : 'Activo'}</p>
                ) : p.comingSoon ? (
                  <p className="text-violet-400/70 text-[10px]">Código preparado</p>
                ) : (
                  <p className="text-red-400/70 text-[10px]">Sin configurar</p>
                )}
              </div>
              {!p.comingSoon && !isConnected && (
                <div className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2 py-1.5 flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>Requiere configuración</span>
                </div>
              )}
              {isConnected && st.last_post && (
                <p className="text-[#94A3B8] text-[10px]">Último post: {st.last_post?.slice(0,10)}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Publer status */}
      <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.10)] rounded-2xl p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
          <Calendar className="w-5 h-5 text-orange-400" />
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold">Publer — Publicador Multi-plataforma</p>
          <p className="text-[#94A3B8] text-xs">
            {statuses.publer?.connected ? `Conectado · ${statuses.publer?.accounts_count ?? 0} cuentas vinculadas` : 'API key configurada — verifica conexión en pestaña Publicador'}
          </p>
        </div>
        <StatusDot connected={statuses.publer?.connected} loading={statsLoading} />
      </div>
    </div>
  );
}

// ─── Tab: Redes Sociales ──────────────────────────────────────────────────────
function RedesSocialesTab({ authFetch, statuses, onRefresh }) {
  const [tiktokLoading, setTiktokLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);

  const startTikTokOAuth = async () => {
    setTiktokLoading(true);
    try {
      const res = await authFetch(`${API}/social/tiktok/oauth/start?owner=neuralia`);
      const data = await res.json();
      if (data.auth_url) window.open(data.auth_url, '_blank');
    } catch (e) {}
    setTiktokLoading(false);
  };

  const startMetaOAuth = async () => {
    setMetaLoading(true);
    try {
      const res = await authFetch(`${API}/social/meta/oauth/start?owner=neuralia`);
      const data = await res.json();
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (e) { setMetaLoading(false); }
  };

  const disconnectMeta = async () => {
    if (!window.confirm('¿Desconectar Instagram y Facebook de Neuralia369?')) return;
    await authFetch(`${API}/social/meta/disconnect?owner=neuralia`, { method: 'DELETE' });
    onRefresh();
  };

  const tiktok = statuses.tiktok || {};
  const meta = statuses.meta || {};
  const metaSocial = statuses.metaSocial || {};

  return (
    <div className="space-y-5" data-testid="redes-sociales-tab">
      {/* TikTok */}
      <div className="bg-[rgba(8,15,32,0.7)] border border-[rgba(148,163,184,0.10)] rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(148,163,184,0.08)]">
          <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center border border-[#fe2c55]/30">
            <Music2 className="w-5 h-5 text-[#fe2c55]" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">TikTok</p>
            <p className="text-[#94A3B8] text-xs">{tiktok.connected ? `@${tiktok.username || 'Conectado'}` : 'Sin conexión'}</p>
          </div>
          <Badge connected={tiktok.connected} />
          <button onClick={() => onRefresh('tiktok')} className="text-[#94A3B8] hover:text-white transition p-1"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {tiktok.connected ? (
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Seguidores', value: tiktok.follower_count?.toLocaleString() || '—' },
                { label: 'Videos', value: tiktok.video_count?.toLocaleString() || '—' },
                { label: 'Likes', value: tiktok.like_count?.toLocaleString() || '—' },
              ].map(m => (
                <div key={m.label} className="bg-[rgba(254,44,85,0.05)] border border-[#fe2c55]/10 rounded-xl p-3 text-center">
                  <p className="text-white font-black text-lg">{m.value}</p>
                  <p className="text-[#94A3B8] text-[10px]">{m.label}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-300 space-y-1">
                  <p className="font-semibold">TikTok en revisión de app</p>
                  <p>Las claves están configuradas (<code className="bg-black/30 px-1 rounded">TIKTOK_CLIENT_KEY</code> ✓). La app está pendiente de aprobación del scope <code className="bg-black/30 px-1 rounded">video.publish</code> por TikTok.</p>
                  <p><strong>Para publicar ahora:</strong> puedes usar el flujo OAuth para conectar tu cuenta propia y publicar en modo "Solo yo" hasta la aprobación.</p>
                </div>
              </div>
              <button onClick={startTikTokOAuth} disabled={tiktokLoading} data-testid="tiktok-connect-btn"
                className="w-full py-2.5 rounded-xl bg-[#fe2c55] hover:bg-[#e0284e] text-white text-sm font-bold transition flex items-center justify-center gap-2 disabled:opacity-50">
                {tiktokLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Conectar cuenta TikTok (OAuth)
              </button>
            </div>
          )}
          <div className="text-xs text-[#94A3B8] space-y-1 border-t border-[rgba(148,163,184,0.08)] pt-3">
            <p className="font-semibold text-white">Para aprobar publicación masiva en TikTok:</p>
            <ol className="space-y-1 list-none">
              <li className="flex gap-2"><span className="text-indigo-400 font-bold">1.</span>Ir a <a href="https://developers.tiktok.com" target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline">developers.tiktok.com</a> → tu app</li>
              <li className="flex gap-2"><span className="text-indigo-400 font-bold">2.</span>En "Scopes" → solicitar aprobación para <code className="bg-black/30 px-1 rounded">video.publish</code> y <code className="bg-black/30 px-1 rounded">video.upload</code></li>
              <li className="flex gap-2"><span className="text-indigo-400 font-bold">3.</span>En "App Details" → añadir URL de política de privacidad y términos</li>
              <li className="flex gap-2"><span className="text-indigo-400 font-bold">4.</span>Enviar a revisión (puede tardar 1-5 días hábiles)</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Meta (Instagram + Facebook) */}
      <div className="bg-[rgba(8,15,32,0.7)] border border-[rgba(148,163,184,0.10)] rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(148,163,184,0.08)]">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#833ab4] to-[#e1306c] flex items-center justify-center">
            <Instagram className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">Meta — Instagram & Facebook</p>
            <p className="text-[#94A3B8] text-xs">
              {meta.connected ? `@${metaSocial?.account?.pages?.[0]?.instagram?.username || metaSocial?.account?.user_name || 'Conectado'}` : `App ID: 1586191503184483`}
            </p>
          </div>
          <Badge connected={meta.connected} />
        </div>
        <div className="p-5 space-y-4">
          {meta.connected ? (
            /* Connected state */
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {(metaSocial?.account?.pages || []).slice(0, 2).map(p => (
                  <div key={p.id} className="bg-emerald-900/10 border border-emerald-500/20 rounded-xl p-3">
                    <p className="text-white text-xs font-semibold">{p.name}</p>
                    {p.instagram && <p className="text-emerald-400 text-[10px]">@{p.instagram.username} · {p.instagram.followers_count?.toLocaleString()} seg.</p>}
                  </div>
                ))}
              </div>
              <button onClick={disconnectMeta}
                className="flex items-center gap-2 text-xs text-red-400 hover:text-red-300 border border-red-500/20 px-3 py-2 rounded-xl transition">
                Desconectar Meta
              </button>
            </div>
          ) : (
            /* Setup / connect state */
            <div className="space-y-4">
              {meta.configured ? (
                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 space-y-3">
                  <p className="text-indigo-300 text-xs font-semibold">¿Tienes tu App Meta lista? Conecta Instagram & Facebook ahora:</p>
                  <button onClick={startMetaOAuth} disabled={metaLoading}
                    data-testid="meta-connect-btn"
                    className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#833ab4] to-[#e1306c] hover:opacity-90 text-white text-sm font-bold transition flex items-center justify-center gap-2 disabled:opacity-50">
                    {metaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Instagram className="w-4 h-4" /> Conectar Instagram & Facebook</>}
                  </button>
                  <p className="text-[#94A3B8] text-[10px]">Te redirigirá a Meta para autorizar. Tu cuenta de Instagram debe ser de tipo Business/Creador.</p>
                </div>
              ) : (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                  <p className="text-amber-300 text-xs">Faltan credenciales Meta en el servidor (META_APP_ID / META_APP_SECRET). Configúralas en el backend.</p>
                </div>
              )}

              <div className="bg-[rgba(8,15,32,0.5)] rounded-xl p-4 space-y-3">
                <p className="text-xs font-bold text-white uppercase tracking-wider">Configurar Meta Embedded Signup (paso a paso):</p>
                <div className="space-y-3">
                  <SetupStep n="1" title="Ir al panel de Meta para Desarrolladores">
                    <p>Abre <a href="https://developers.facebook.com/apps/1586191503184483" target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline">developers.facebook.com/apps/1586191503184483</a></p>
                    <p>Inicia sesión con la cuenta admin de Neuralia369</p>
                  </SetupStep>
                  <SetupStep n="2" title="Añadir producto WhatsApp Business">
                    <p>Panel lateral → "Añadir producto" → seleccionar <strong className="text-white">WhatsApp</strong></p>
                    <p>En la sección WhatsApp → "API Setup" → anota el <strong className="text-white">Phone Number ID</strong> y <strong className="text-white">Access Token</strong></p>
                  </SetupStep>
                  <SetupStep n="3" title="Poner el App ID en Embedded Signup">
                    <p>En tu panel de Meta → "WhatsApp" → "Embedded Signup" → copia el <strong className="text-white">App ID</strong></p>
                    <p>Es tu mismo App ID: <code className="bg-black/30 px-1.5 py-0.5 rounded text-indigo-300">1586191503184483</code> <CopyBtn text="1586191503184483" /></p>
                    <p className="text-amber-300">→ Pégalo en el campo <code>META_APP_ID_EMBEDDED_SIGNUP</code> del servidor</p>
                  </SetupStep>
                  <SetupStep n="4" title="Verificar el dominio">
                    <p>En Meta → "Verificación del dominio" → añadir: <code className="bg-black/30 px-1.5 py-0.5 rounded text-indigo-300">neuralia369.com</code> <CopyBtn text="neuralia369.com" /></p>
                    <p>En "Inicio de sesión con Facebook" → "URI de redireccionamiento de OAuth válidos" → añadir: <code className="bg-black/30 px-1.5 py-0.5 rounded text-indigo-300">https://neuralia369.com/meta/oauth/callback</code> <CopyBtn text="https://neuralia369.com/meta/oauth/callback" /></p>
                  </SetupStep>
                  <SetupStep n="5" title="Instagram Graph API">
                    <p>En tu app Meta → "Instagram Basic Display" o "Instagram Graph API" → conectar tu cuenta de Instagram Business</p>
                    <p>Necesitas: cuenta IG Business + página de Facebook conectada</p>
                  </SetupStep>
                </div>
              </div>
            </div>
          )}
          <a href="https://developers.facebook.com/apps/1586191503184483" target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:text-white hover:border-white/20 text-sm font-semibold transition">
            Abrir Meta Developer Console <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* LinkedIn + Twitter placeholders */}
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { key: 'linkedin', platform: PLATFORMS.linkedin, msg: 'Crea tu cuenta LinkedIn Business en linkedin.com/company y vuelve para conectarla.' },
          { key: 'twitter', platform: PLATFORMS.twitter, msg: 'Crea tu cuenta Twitter/X Business en x.com y solicita acceso a la API v2 en developer.x.com.' },
        ].map(({ key, platform, msg }) => (
          <div key={key} className="bg-[rgba(8,15,32,0.5)] border border-violet-500/10 rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${platform.gradient} text-white`}>
                <platform.icon />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold">{platform.name}</p>
                <Badge comingSoon />
              </div>
            </div>
            <p className="text-[#94A3B8] text-xs leading-relaxed">{msg}</p>
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl px-3 py-2 text-xs text-violet-300">
              Código OAuth preparado y listo. Solo necesita credenciales para activarse.
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tab: WhatsApp (Meta Business API) ───────────────────────────────────────
function WhatsAppTab({ authFetch, statuses }) {
  const [config, setConfig] = useState({ token: '', phone_id: '' });
  const [saving, setSaving] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testStatus, setTestStatus] = useState(null);
  const wa = statuses.whatsapp || {};

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/admin/meta-wa/config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wa_token: config.token, wa_phone_id: config.phone_id })
      });
      const data = await res.json();
      if (data.success) setTestStatus({ ok: true, msg: '¡Configuración guardada!' });
      else setTestStatus({ ok: false, msg: data.detail || 'Error al guardar' });
    } catch (e) { setTestStatus({ ok: false, msg: 'Error al guardar' }); }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testPhone) return;
    try {
      const res = await authFetch(`${API}/admin/meta-wa/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: testPhone, message: testMsg || 'Hola desde Neuralia369 Academy! ✅' })
      });
      const data = await res.json();
      setTestStatus({ ok: data.success, msg: data.success ? '¡Mensaje enviado correctamente!' : (data.detail || 'Error') });
    } catch (e) { setTestStatus({ ok: false, msg: 'Error de conexión' }); }
  };

  return (
    <div className="space-y-5" data-testid="whatsapp-tab">
      {/* Free option: Meta WhatsApp Business Cloud API */}
      <div className="bg-emerald-900/10 border border-emerald-500/20 rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#25D366]/20 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-[#25D366]" />
          </div>
          <div>
            <p className="text-white font-semibold">Meta WhatsApp Business Cloud API</p>
            <p className="text-emerald-400 text-xs">GRATIS hasta 1.000 conversaciones/mes · Sin coste de setup</p>
          </div>
          <Badge connected={wa.connected} />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Setup guide */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-white uppercase tracking-wider">Activar WhatsApp gratis (paso a paso)</p>
            <div className="space-y-3">
              <SetupStep n="1" title="Ir a Meta for Developers">
                <p><a href="https://developers.facebook.com/apps/1586191503184483/whatsapp-business/wa-dev-console/" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">Abrir WhatsApp Business Console →</a></p>
              </SetupStep>
              <SetupStep n="2" title="Añadir número de teléfono">
                <p>En "API Setup" → "Add Phone Number" → usa un número no registrado en WhatsApp</p>
                <p>Verifica el número con el código SMS que recibirás</p>
              </SetupStep>
              <SetupStep n="3" title="Obtener Phone Number ID y Token">
                <p>Copia el <strong className="text-white">Phone Number ID</strong> (ej: 123456789012345)</p>
                <p>En "System Users" → genera un token permanente con permisos <code className="bg-black/30 px-1 rounded">whatsapp_business_messaging</code></p>
              </SetupStep>
              <SetupStep n="4" title="Añadir credenciales abajo">
                <p>Pega el token y Phone ID en los campos de la derecha y haz click en Guardar</p>
              </SetupStep>
            </div>
          </div>

          {/* Config form */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-white uppercase tracking-wider">Configuración</p>
            <div className="space-y-2.5">
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Token de acceso permanente</label>
                <input value={config.token} onChange={e => setConfig(p => ({ ...p, token: e.target.value }))}
                  placeholder="EAAxxxxx..."
                  type="password"
                  className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] focus:border-emerald-500/40 rounded-xl px-3 py-2.5 text-white text-xs placeholder-[#94A3B8]/40 focus:outline-none transition" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Phone Number ID</label>
                <input value={config.phone_id} onChange={e => setConfig(p => ({ ...p, phone_id: e.target.value }))}
                  placeholder="123456789012345"
                  className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] focus:border-emerald-500/40 rounded-xl px-3 py-2.5 text-white text-xs placeholder-[#94A3B8]/40 focus:outline-none transition" />
              </div>
              <button onClick={save} disabled={!config.token || !config.phone_id || saving}
                className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-bold transition flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar configuración'}
              </button>
              {testStatus && (
                <p className={`text-xs px-2.5 py-1.5 rounded-lg ${testStatus.ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{testStatus.msg}</p>
              )}
            </div>

            {/* Test send */}
            <div className="border-t border-[rgba(148,163,184,0.08)] pt-3 space-y-2">
              <p className="text-xs font-bold text-white">Enviar mensaje de prueba</p>
              <input value={testPhone} onChange={e => setTestPhone(e.target.value)}
                placeholder="+51987654321"
                className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] rounded-xl px-3 py-2 text-white text-xs placeholder-[#94A3B8]/40 focus:outline-none" />
              <input value={testMsg} onChange={e => setTestMsg(e.target.value)}
                placeholder="Mensaje de prueba..."
                className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] rounded-xl px-3 py-2 text-white text-xs placeholder-[#94A3B8]/40 focus:outline-none" />
              <button onClick={sendTest} disabled={!testPhone}
                className="w-full py-2 rounded-xl border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs font-bold transition disabled:opacity-40 flex items-center justify-center gap-2">
                <Send className="w-3.5 h-3.5" /> Enviar prueba
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Make.com alternative */}
      <div className="bg-[rgba(8,15,32,0.5)] border border-orange-500/20 rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <p className="text-white font-semibold">Alternativa: Make.com + WhatsApp</p>
            <p className="text-[#94A3B8] text-xs">Ya tienes Make.com configurado — úsalo para enviar WhatsApp sin coste adicional</p>
          </div>
        </div>
        <p className="text-xs text-[#94A3B8] leading-relaxed">
          En Make.com puedes conectar WhatsApp Business (Meta) y crear un escenario que escuche los webhooks de Neuralia369 y envíe los mensajes automáticamente. 
          Esto es 100% gratuito con tu cuenta actual de Make.com.
        </p>
        <Link to="/admin?tab=integrations" className="inline-flex items-center gap-2 text-xs text-orange-400 hover:text-orange-300 transition font-semibold">
          Gestionar webhooks Make.com <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

// ─── Tab: Publicador (Publer) ─────────────────────────────────────────────────
function PublicadorTab({ authFetch, statuses }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ content: '', platforms: ['instagram'], schedule: '' });
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    authFetch(`${API}/admin/publer/posts?workspace=neuralia`).then(r => r.json())
      .then(d => { setPosts(d.posts || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [authFetch]);

  const togglePlatform = (p) => setForm(f => ({
    ...f, platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p]
  }));

  const publish = async () => {
    if (!form.content.trim()) return;
    setPublishing(true); setResult(null);
    try {
      const res = await authFetch(`${API}/admin/publer/publish`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: form.content, platforms: form.platforms, schedule_at: form.schedule || null, workspace: 'neuralia' })
      });
      const data = await res.json();
      setResult({ ok: data.success, msg: data.success ? '¡Publicado / programado correctamente!' : (data.detail || 'Error') });
      if (data.success) setForm(f => ({ ...f, content: '', schedule: '' }));
    } catch (e) { setResult({ ok: false, msg: 'Error al publicar' }); }
    setPublishing(false);
  };

  const PUBLER_PLATFORMS = [
    { id: 'instagram', label: 'Instagram', gradient: 'from-[#833ab4] to-[#e1306c]', icon: Instagram },
    { id: 'facebook', label: 'Facebook', gradient: 'from-[#1877F2] to-[#0e5cc5]', icon: Globe },
    { id: 'tiktok', label: 'TikTok', gradient: 'from-black to-[#2d2d2d]', icon: Music2 },
    { id: 'linkedin', label: 'LinkedIn', gradient: 'from-[#0A66C2] to-[#085199]', icon: Linkedin },
    { id: 'twitter', label: 'Twitter/X', gradient: 'from-black to-[#222]', icon: Twitter },
  ];

  return (
    <div className="space-y-5" data-testid="publicador-tab">
      <div className="grid md:grid-cols-5 gap-3 items-start">
        {/* Publer status */}
        <div className="md:col-span-2 space-y-4">
          <div className={`rounded-2xl p-4 border ${statuses.publer?.connected ? 'bg-orange-500/10 border-orange-500/20' : 'bg-[rgba(8,15,32,0.5)] border-[rgba(148,163,184,0.10)]'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-orange-400" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">Publer</p>
                <p className="text-[#94A3B8] text-[10px]">{statuses.publer?.connected ? 'API conectada ✓' : 'Verificando...'}</p>
              </div>
            </div>
            {!statuses.publer?.connected && (
              <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 space-y-1.5">
                <p className="font-semibold">Para activar Publer:</p>
                <ol className="space-y-1 list-none">
                  <li>1. Ir a <a href="https://publer.io" target="_blank" rel="noreferrer" className="underline">publer.io</a> → Settings → API</li>
                  <li>2. La API key ya está configurada en el servidor</li>
                  <li>3. Conecta tus cuentas sociales en Publer directamente</li>
                  <li>4. Vuelve aquí y refresca</li>
                </ol>
              </div>
            )}
          </div>

          {/* Scheduled posts */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-white uppercase tracking-wider">Posts programados ({posts.length})</p>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#94A3B8] animate-spin" /></div>
            ) : posts.length === 0 ? (
              <p className="text-[#94A3B8] text-xs py-4 text-center">Sin posts programados</p>
            ) : posts.slice(0, 5).map((p, i) => (
              <div key={i} className="bg-[rgba(8,15,32,0.5)] border border-[rgba(148,163,184,0.08)] rounded-xl px-3 py-2.5">
                <p className="text-white text-xs font-medium truncate">{p.text || p.content || '(Sin texto)'}</p>
                <p className="text-[#94A3B8] text-[10px] mt-0.5">{p.scheduled_at || p.created_at}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick publish form */}
        <div className="md:col-span-3 bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.10)] rounded-2xl p-5 space-y-4">
          <p className="text-white font-semibold">Publicación rápida</p>

          {/* Platform selector */}
          <div className="space-y-2">
            <p className="text-xs text-[#94A3B8]">Plataformas</p>
            <div className="flex flex-wrap gap-2">
              {PUBLER_PLATFORMS.map(p => (
                <button key={p.id} onClick={() => togglePlatform(p.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold border transition ${form.platforms.includes(p.id) ? `bg-gradient-to-r ${p.gradient} text-white border-transparent` : 'border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:border-white/20'}`}>
                  <p.icon className="w-3 h-3" /> {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-1.5">
            <label className="text-xs text-[#94A3B8]">Contenido del post</label>
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={5}
              placeholder="Escribe tu post aquí... Añade hashtags, emojis, call to action..."
              className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] focus:border-orange-500/40 rounded-xl px-4 py-3 text-white text-sm placeholder-[#94A3B8]/40 resize-none focus:outline-none transition"
              data-testid="publer-content" />
            <p className="text-[10px] text-[#94A3B8] text-right">{form.content.length}/2200 caracteres</p>
          </div>

          {/* Schedule */}
          <div className="space-y-1.5">
            <label className="text-xs text-[#94A3B8]">Programar para (opcional)</label>
            <input type="datetime-local" value={form.schedule} onChange={e => setForm(f => ({ ...f, schedule: e.target.value }))}
              className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] focus:border-orange-500/40 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none transition" />
          </div>

          {result && (
            <div className={`text-xs px-3 py-2 rounded-xl ${result.ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{result.msg}</div>
          )}
          <button onClick={publish} disabled={!form.content.trim() || publishing || form.platforms.length === 0}
            data-testid="publer-publish-btn"
            className="w-full py-3 rounded-2xl bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white font-bold transition flex items-center justify-center gap-2">
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : form.schedule ? <><Calendar className="w-4 h-4" /> Programar post</> : <><Send className="w-4 h-4" /> Publicar ahora</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Studio (Crea & Publica) ────────────────────────────────────────────
function StudioTab({ authFetch }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ script: '', voice_id: '', visual: 'fal_avatar', caption: '', platforms: ['tiktok', 'instagram'] });
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  const STEPS = [
    { n: 1, label: 'Guion', icon: Sparkles },
    { n: 2, label: 'Voz', icon: Mic },
    { n: 3, label: 'Vídeo', icon: Video },
    { n: 4, label: 'Publicar', icon: Send },
  ];

  useEffect(() => {
    if (step !== 2 || voices.length > 0) return;
    setLoadingVoices(true);
    authFetch(`${API}/admin/production-os/voices/catalog`).then(r => r.json())
      .then(d => { setVoices(d.voices || []); setLoadingVoices(false); })
      .catch(() => setLoadingVoices(false));
  }, [step, voices.length, authFetch]);

  const generate = async () => {
    if (!form.script.trim()) return;
    setGenerating(true); setResult(null);
    try {
      const res = await authFetch(`${API}/workspace/videos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          script: form.script,
          voice_id: form.voice_id || 'default',
          provider_model: form.visual === 'fal_avatar' ? 'fal-ai/kling-video/ai-avatar/v2/pro' : 'creatomate',
          output_format: 'mp4',
        })
      });
      const data = await res.json();
      setResult({ success: true, video_url: data.output_url || data.video_url, job_id: data.job_id });
      setStep(4);
    } catch (e) {
      setResult({ success: false, error: 'Error al generar. Verifica las credenciales de Fal.ai / Creatomate.' });
    }
    setGenerating(false);
  };

  const publishVideo = async () => {
    if (!result?.video_url) return;
    setPublishing(true); setPublishResult(null);
    try {
      const res = await authFetch(`${API}/admin/publer/publish`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: form.caption || form.script.slice(0, 100) + '...',
          platforms: form.platforms,
          media_url: result.video_url,
          workspace: 'neuralia',
        })
      });
      const data = await res.json();
      setPublishResult({ ok: data.success, msg: data.success ? '¡Vídeo publicado en las redes seleccionadas!' : (data.detail || 'Error') });
    } catch (e) { setPublishResult({ ok: false, msg: 'Error al publicar' }); }
    setPublishing(false);
  };

  const STUDIO_PLATFORMS = [
    { id: 'tiktok', label: 'TikTok', icon: Music2 },
    { id: 'instagram', label: 'Instagram', icon: Instagram },
    { id: 'facebook', label: 'Facebook', icon: Globe },
    { id: 'youtube', label: 'YouTube', icon: Youtube, disabled: true },
  ];

  return (
    <div className="space-y-5" data-testid="studio-tab">
      {/* Step indicator */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            <button onClick={() => s.n <= step && setStep(s.n)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold whitespace-nowrap transition ${step === s.n ? 'bg-indigo-600 text-white' : s.n < step ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/20' : 'bg-[rgba(8,15,32,0.5)] text-[#94A3B8] border border-[rgba(148,163,184,0.10)]'}`}>
              <s.icon className="w-4 h-4" />
              {s.n}. {s.label}
              {s.n < step && <CheckCircle className="w-3.5 h-3.5" />}
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Script */}
      {step === 1 && (
        <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.10)] rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            <p className="text-white font-semibold">Guion del vídeo</p>
          </div>
          <textarea value={form.script} onChange={e => setForm(f => ({ ...f, script: e.target.value }))} rows={7}
            placeholder="Escribe el guion de tu vídeo aquí...&#10;&#10;Ejemplo: '¿Sabías que la inteligencia artificial puede transformar tu negocio en 30 días? Hoy te cuento cómo Neuralia369 Academy puede ayudarte...'&#10;&#10;Tip: 30-60 segundos de guion = ~80-150 palabras"
            data-testid="studio-script"
            className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] focus:border-indigo-500/40 rounded-xl px-4 py-3 text-white text-sm placeholder-[#94A3B8]/40 resize-none focus:outline-none transition" />
          <div className="flex items-center justify-between">
            <p className="text-[#94A3B8] text-xs">{form.script.split(/\s+/).filter(Boolean).length} palabras · ~{Math.ceil(form.script.split(/\s+/).filter(Boolean).length / 2.5)}s</p>
            <button onClick={() => setStep(2)} disabled={!form.script.trim()}
              className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-sm font-bold transition flex items-center gap-2">
              Siguiente: Elegir voz <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Voice */}
      {step === 2 && (
        <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.10)] rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-violet-400" />
            <p className="text-white font-semibold">Seleccionar voz (ElevenLabs)</p>
          </div>
          {loadingVoices ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {(voices.length > 0 ? voices : [
                { voice_id: 'Xb7hH8MSUJpSbSDYk0k2', name: 'Alice (EN)', labels: { accent: 'British' } },
                { voice_id: 'pqHfZKP75CvOlD17v77K', name: 'Aria (EN)', labels: {} },
                { voice_id: 'IKne3meq5aSn9XLyUdCD', name: 'Charlie (EN)', labels: {} },
                { voice_id: 'nPczCjzI2devNBz1zQrb', name: 'Brian (EN)', labels: {} },
                { voice_id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella (EN)', labels: {} },
                { voice_id: 'default', name: 'Voz por defecto', labels: {} },
              ]).map(v => (
                <button key={v.voice_id} onClick={() => setForm(f => ({ ...f, voice_id: v.voice_id }))}
                  className={`text-left px-3 py-2.5 rounded-xl border text-sm transition ${form.voice_id === v.voice_id ? 'bg-violet-600 border-violet-500 text-white' : 'border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:border-violet-500/40 hover:text-white'}`}>
                  <p className="font-medium text-xs">{v.name}</p>
                  {v.labels?.accent && <p className="text-[10px] opacity-70">{v.labels.accent}</p>}
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="px-4 py-2 rounded-xl border border-[rgba(148,163,184,0.14)] text-[#94A3B8] text-sm hover:text-white transition">← Atrás</button>
            <button onClick={() => setStep(3)}
              className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold transition flex items-center gap-2">
              Siguiente: Visual <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Visual */}
      {step === 3 && (
        <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.10)] rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-400" />
            <p className="text-white font-semibold">Estilo visual del vídeo</p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { id: 'fal_avatar', label: 'Avatar IA (Fal.ai)', desc: 'Avatar generado por IA con tu guion como narración. Kling AI v2 Pro.', icon: '🤖', recommended: true },
              { id: 'creatomate', label: 'Plantilla Creatomate', desc: 'Vídeo con texto animado, imágenes y subtítulos automáticos.', icon: '🎬' },
            ].map(o => (
              <button key={o.id} onClick={() => setForm(f => ({ ...f, visual: o.id }))}
                className={`text-left p-4 rounded-2xl border transition ${form.visual === o.id ? 'bg-blue-600/20 border-blue-500/40' : 'border-[rgba(148,163,184,0.14)] hover:border-blue-500/20'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{o.icon}</span>
                  <p className="text-white text-sm font-semibold">{o.label}</p>
                  {o.recommended && <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full font-bold">Recomendado</span>}
                </div>
                <p className="text-[#94A3B8] text-xs leading-relaxed">{o.desc}</p>
              </button>
            ))}
          </div>
          {result?.error && <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{result.error}</p>}
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="px-4 py-2 rounded-xl border border-[rgba(148,163,184,0.14)] text-[#94A3B8] text-sm hover:text-white transition">← Atrás</button>
            <button onClick={generate} disabled={generating}
              data-testid="generate-video-btn"
              className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-bold transition flex items-center gap-2">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando vídeo...</> : <><Video className="w-4 h-4" /> Generar vídeo</>}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Publish */}
      {step === 4 && (
        <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.10)] rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-400" />
            <p className="text-white font-semibold">Publicar vídeo</p>
          </div>
          {result?.video_url && (
            <div className="aspect-video max-h-40 bg-black rounded-xl overflow-hidden flex items-center justify-center">
              <video src={result.video_url} controls className="w-full h-full object-contain" />
            </div>
          )}
          <div className="space-y-2">
            <label className="text-xs text-[#94A3B8]">Caption / descripción</label>
            <textarea value={form.caption} onChange={e => setForm(f => ({ ...f, caption: e.target.value }))} rows={3}
              placeholder="Descripción del vídeo, hashtags..."
              className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] rounded-xl px-3 py-2.5 text-white text-sm placeholder-[#94A3B8]/40 focus:outline-none resize-none" />
          </div>
          <div className="flex flex-wrap gap-2">
            {STUDIO_PLATFORMS.map(p => (
              <button key={p.id} onClick={() => !p.disabled && setForm(f => ({ ...f, platforms: f.platforms.includes(p.id) ? f.platforms.filter(x => x !== p.id) : [...f.platforms, p.id] }))}
                disabled={p.disabled}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${form.platforms.includes(p.id) && !p.disabled ? 'bg-emerald-600/20 border-emerald-500/30 text-emerald-400' : 'border-[rgba(148,163,184,0.14)] text-[#94A3B8]'} ${p.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-white/20 cursor-pointer'}`}>
                <p.icon className="w-3 h-3" /> {p.label}
              </button>
            ))}
          </div>
          {publishResult && (
            <div className={`text-xs px-3 py-2 rounded-xl ${publishResult.ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{publishResult.msg}</div>
          )}
          <div className="flex justify-between">
            <button onClick={() => { setStep(1); setResult(null); setPublishResult(null); }} className="px-4 py-2 rounded-xl border border-[rgba(148,163,184,0.14)] text-[#94A3B8] text-sm hover:text-white transition">Nuevo vídeo</button>
            <button onClick={publishVideo} disabled={publishing || !result?.video_url || form.platforms.length === 0}
              data-testid="publish-video-btn"
              className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-bold transition flex items-center gap-2">
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Publicar en {form.platforms.length} red{form.platforms.length !== 1 ? 'es' : ''}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Clientes ────────────────────────────────────────────────────────────
function ClientesRedesTab({ authFetch }) {
  const [clients, setClients] = useState([]);
  const [waConns, setWaConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthResult, setHealthResult] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      authFetch(`${API}/admin/connections/whatsapp`).then(r => r.json()).catch(() => ({ connections: [] })),
      authFetch(`${API}/admin/users`).then(r => r.json()).catch(() => ({ users: [] })),
    ]).then(([wa, cl]) => {
      setWaConns(wa.connections || []);
      setClients(cl.users || cl.clients || []);
      setLoading(false);
    });
  }, [authFetch]);

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const res = await authFetch(`${API}/admin/connections/health-check`, { method: 'POST' });
      const data = await res.json();
      setHealthResult(data);
    } catch (e) { setHealthResult({ success: false, error: 'Error al ejecutar el check' }); }
    setHealthLoading(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" /></div>;

  return (
    <div className="space-y-5" data-testid="clientes-redes-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-white font-semibold">Estado de conexiones por cliente</p>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#94A3B8] bg-[rgba(8,15,32,0.5)] border border-[rgba(148,163,184,0.08)] rounded-lg px-2.5 py-1">{waConns.length} con WhatsApp</span>
          <button onClick={runHealthCheck} disabled={healthLoading} data-testid="run-health-check-btn"
            className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 text-xs font-bold transition disabled:opacity-50">
            {healthLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>⚡</span>}
            Verificar conexiones
          </button>
        </div>
      </div>

      {/* Health check result */}
      {healthResult && (
        <div className={`rounded-xl border p-4 ${healthResult.issues_count > 0 ? 'border-red-500/20 bg-red-500/5' : 'border-emerald-500/20 bg-emerald-500/5'}`} data-testid="health-check-result">
          <div className="flex items-center gap-2 mb-2">
            <span>{healthResult.issues_count > 0 ? '🔴' : '🟢'}</span>
            <p className="text-white text-sm font-bold">
              {healthResult.issues_count > 0 ? `${healthResult.issues_count} conexión(es) caída(s) detectada(s)` : 'Todas las conexiones funcionan correctamente'}
            </p>
            {healthResult.alert_sent && <span className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">Alerta enviada por WhatsApp</span>}
          </div>
          {(healthResult.issues || []).map((iss, i) => (
            <div key={i} className="text-xs text-red-400 mt-1">• {iss.client_label} → {iss.network}: {iss.current_status}</div>
          ))}
          <p className="text-[#94A3B8] text-[10px] mt-2">{healthResult.ts?.slice(0,16).replace('T',' ')} UTC · {healthResult.checked_clients} clientes verificados</p>
        </div>
      )}

      {waConns.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl border border-[rgba(148,163,184,0.10)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[rgba(8,15,32,0.8)] border-b border-[rgba(148,163,184,0.08)]">
                {['Cliente', 'WhatsApp', 'Número', 'Estado', 'Conectado'].map(h => (
                  <th key={h} className="text-left text-[10px] text-[#94A3B8] font-bold uppercase tracking-wider px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {waConns.map((c, i) => (
                <tr key={i} className="border-b border-[rgba(148,163,184,0.05)] hover:bg-[rgba(148,163,184,0.03)] transition">
                  <td className="px-4 py-3 text-white text-xs">{c.display_name || c.client_id || 'Cliente'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-emerald-400 text-xs">Conectado</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{c.phone_number || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/20">{c.status || 'active'}</span>
                  </td>
                  <td className="px-4 py-3 text-[#94A3B8] text-[10px]">{c.connected_at?.slice(0,10) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-[rgba(148,163,184,0.05)] border border-[rgba(148,163,184,0.10)] flex items-center justify-center mx-auto">
            <Users className="w-7 h-7 text-[#94A3B8]/40" />
          </div>
          <p className="text-white font-semibold">Sin conexiones de clientes aún</p>
          <p className="text-[#94A3B8] text-sm max-w-sm mx-auto">Los clientes se conectan desde su panel usando el Embedded Signup de Meta WhatsApp o desde su portal de conexiones.</p>
        </div>
      )}

      {/* Info box */}
      <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-4 text-xs text-indigo-300 space-y-2">
        <p className="font-bold text-white">Notificaciones de fallos de red a tu WhatsApp</p>
        <p>Cuando detectemos un fallo en la red de un cliente, el sistema enviará un mensaje automático a tu número. Para activarlo:</p>
        <ol className="space-y-1">
          <li className="flex gap-2"><span className="font-bold">1.</span>Configura tu WhatsApp Business en la pestaña "WhatsApp" de este panel</li>
          <li className="flex gap-2"><span className="font-bold">2.</span>El sistema ya tiene el código de notificación listo — se activará automáticamente al guardar el token</li>
        </ol>
      </div>
    </div>
  );
}

// ─── Main Hub ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'resumen', label: 'Resumen', icon: BarChart3 },
  { id: 'redes', label: 'Redes Sociales', icon: Globe },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { id: 'publicador', label: 'Publicador', icon: Calendar },
  { id: 'studio', label: 'Studio · Crea & Publica', icon: Sparkles },
  { id: 'clientes', label: 'Clientes', icon: Users },
];

export default function AdminRedesPage() {
  const { authFetch } = useAuth();
  const [tab, setTab] = useState('resumen');
  const [statuses, setStatuses] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);

  const loadStatuses = async (platform = 'all') => {
    setStatsLoading(true);
    try {
      const results = await Promise.allSettled([
        authFetch(`${API}/social/tiktok/status?owner=neuralia`).then(r => r.json()),
        authFetch(`${API}/admin/publer/status?workspace=neuralia`).then(r => r.json()),
        authFetch(`${API}/admin/meta-wa/status`).then(r => r.json()),
        authFetch(`${API}/social/meta/status?owner=neuralia`).then(r => r.json()),
      ]);
      const [tiktok, publer, metaWa, metaSocial] = results.map(r => r.status === 'fulfilled' ? r.value : {});
      setStatuses({
        tiktok: { connected: tiktok?.connected, username: tiktok?.username, follower_count: tiktok?.follower_count, video_count: tiktok?.video_count, like_count: tiktok?.like_count, last_post: tiktok?.last_post },
        publer: { connected: publer?.connected, scheduled_count: publer?.scheduled_count, accounts_count: publer?.accounts_count },
        whatsapp: { connected: metaWa?.configured },
        meta: { connected: metaSocial?.connected, username: metaSocial?.account?.pages?.[0]?.instagram?.username, configured: metaSocial?.configured },
        metaWa: { configured: metaWa?.configured, has_token: metaWa?.has_token, phone_id_hint: metaWa?.phone_id_hint },
        metaSocial: metaSocial,
      });
    } catch (e) {}
    setStatsLoading(false);
  };

  useEffect(() => { loadStatuses(); }, []);

  return (
    <div className="min-h-screen bg-[#030712] text-white">
      {/* Header */}
      <div className="border-b border-[rgba(148,163,184,0.08)] bg-[rgba(8,15,32,0.9)] backdrop-blur-sm px-6 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white">Hub de Redes & Publicación</h1>
              <p className="text-[#94A3B8] text-xs">Gestiona todas las conexiones sociales de Neuralia369</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => loadStatuses()} disabled={statsLoading}
              className="flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-white border border-[rgba(148,163,184,0.14)] px-3 py-2 rounded-xl transition disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${statsLoading ? 'animate-spin' : ''}`} /> Refrescar
            </button>
            <Link to="/admin" className="text-xs text-[#94A3B8] hover:text-white border border-[rgba(148,163,184,0.14)] px-3 py-2 rounded-xl transition">
              ← Admin
            </Link>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[rgba(148,163,184,0.08)] px-6">
        <div className="max-w-7xl mx-auto flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`redes-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition ${tab === t.id ? 'border-indigo-400 text-white' : 'border-transparent text-[#94A3B8] hover:text-white'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {tab === 'resumen'   && <ResumenTab authFetch={authFetch} statuses={statuses} statsLoading={statsLoading} />}
        {tab === 'redes'     && <RedesSocialesTab authFetch={authFetch} statuses={statuses} onRefresh={loadStatuses} />}
        {tab === 'whatsapp'  && <WhatsAppTab authFetch={authFetch} statuses={statuses} />}
        {tab === 'publicador'&& <PublicadorTab authFetch={authFetch} statuses={statuses} />}
        {tab === 'studio'    && <StudioTab authFetch={authFetch} />}
        {tab === 'clientes'  && <ClientesRedesTab authFetch={authFetch} />}
      </div>
    </div>
  );
}
